// Parser de carga masiva de miembros — puro (sin Firebase ni DOM).
// Lo usan la pantalla de Carga Masiva (navegador) y el script de importación (Node).
//
// Formato esperado (pegado desde el Excel, separado por TAB), con o sin encabezado:
//   Timestamp | Nombre | RUT alumno | Fecha nac | Categoría | Nombre apoderado | Teléfono apoderado
// (El encabezado original rotula la última columna como "Rut" pero contiene teléfonos.)
import { normalizeRut, isValidRut, formatRut } from "./format";

export interface ParsedAlumno {
  alumnoNombre: string;
  rut: string; // normalizado (sin puntos, con guion)
  rutValido: boolean;
  fechaNacimiento: string | null; // ISO yyyy-mm-dd
  categoria: string; // primaria
  categorias: string[]; // todas
  apoderadoNombre: string;
  apoderadoTelefono: string; // normalizado
  warnings: string[];
}

export interface ParseResult {
  rows: ParsedAlumno[];
  totalLineas: number;
}

/** Normaliza un teléfono chileno; toma el primer número si hay varios/anotaciones. */
export function normalizeTelefono(raw: string): string {
  if (!raw) return "";
  // Corta anotaciones ("mamá", "papá") y todo lo posterior.
  const sinNotas = raw.split(/mam|pap|\n/i)[0];
  let d = sinNotas.replace(/\D/g, "");
  if (d.length > 9 && d.startsWith("56")) d = d.slice(2); // prefijo país
  d = d.replace(/^0+/, "");
  if (d.length < 8) return sinNotas.trim(); // no parece teléfono válido, deja lo escrito
  d = d.slice(0, 9); // primer número, 9 dígitos (móvil chileno)
  const p1 = d.slice(0, 1), p2 = d.slice(1, 5), p3 = d.slice(5, 9);
  return `+56 ${[p1, p2, p3].filter(Boolean).join(" ")}`;
}

/** Parsea una fecha d/m/aaaa flexible a ISO. Corrige años tipo "0011" -> 2011. */
export function parseFechaFlexible(raw: string): string | null {
  if (!raw) return null;
  const m = raw.trim().match(/^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{2,4})$/);
  if (!m) return null;
  let [, dd, mm, yyyy] = m;
  let year = parseInt(yyyy, 10);
  if (year < 100) year += 2000; // 11 -> 2011, 0011 -> 2011
  const d = new Date(year, parseInt(mm, 10) - 1, parseInt(dd, 10));
  if (isNaN(d.getTime())) return null;
  return `${year}-${String(mm).padStart(2, "0")}-${String(dd).padStart(2, "0")}`;
}

function isHeader(cols: string[]): boolean {
  const joined = cols.join(" ").toLowerCase();
  return joined.includes("nombre") && joined.includes("rut") && joined.includes("categoria");
}

/** Parsea el texto pegado (TAB-separado) en filas de alumnos. Separa mellizos y categorías múltiples. */
export function parseMiembros(text: string): ParseResult {
  const lineas = text.split(/\r?\n/).map((l) => l).filter((l) => l.trim().length > 0);
  const rows: ParsedAlumno[] = [];

  for (const linea of lineas) {
    const cols = linea.split("\t").map((c) => c.trim());
    if (cols.length < 5) continue; // fila incompleta
    if (isHeader(cols)) continue;

    // Si hay 7 columnas, la 1ª es timestamp; si hay 6, no hay timestamp.
    const off = cols.length >= 7 ? 1 : 0;
    const nombreRaw = cols[off] || "";
    const rutRaw = cols[off + 1] || "";
    const fechaRaw = cols[off + 2] || "";
    const categoriaRaw = cols[off + 3] || "";
    const apoNombre = cols[off + 4] || "";
    const telRaw = cols[off + 5] || "";

    const categorias = categoriaRaw.split(",").map((c) => c.trim().toUpperCase()).filter(Boolean);
    const fechaNacimiento = parseFechaFlexible(fechaRaw);
    const apoderadoTelefono = normalizeTelefono(telRaw);

    // ¿Mellizos? Nombre con " y " y dos RUTs separados por "/".
    const rutParts = rutRaw.split("/").map((r) => r.trim()).filter(Boolean);
    const nombreParts = nombreRaw.split(/\s+y\s+/i).map((n) => n.trim()).filter(Boolean);
    const esMultiple = rutParts.length > 1 && nombreParts.length === rutParts.length;
    const pares = esMultiple
      ? nombreParts.map((n, i) => ({ nombre: n, rut: rutParts[i] }))
      : [{ nombre: nombreRaw, rut: rutRaw }];

    for (const par of pares) {
      const warnings: string[] = [];
      const rutNorm = normalizeRut(par.rut);
      const rutValido = isValidRut(par.rut);
      if (!par.rut) warnings.push("Sin RUT");
      else if (!rutValido) warnings.push(`RUT con dígito verificador inválido (${formatRut(par.rut)})`);
      if (!fechaNacimiento && fechaRaw) warnings.push(`Fecha de nacimiento no reconocida (${fechaRaw})`);
      if (!apoNombre) warnings.push("Sin nombre de apoderado");
      if (!apoderadoTelefono) warnings.push("Sin teléfono de apoderado");
      warnings.push("Falta correo del apoderado (necesario para el portal)");
      if (esMultiple) warnings.push("Fila de mellizos/hermanos: separada automáticamente");
      if (categorias.length > 1) warnings.push(`Varias categorías: se usa "${categorias[0]}", resto en observación`);

      rows.push({
        alumnoNombre: par.nombre.replace(/\s+/g, " ").trim(),
        rut: rutNorm,
        rutValido,
        fechaNacimiento,
        categoria: categorias[0] || "",
        categorias,
        apoderadoNombre: apoNombre,
        apoderadoTelefono,
        warnings,
      });
    }
  }

  return { rows, totalLineas: lineas.length };
}
